import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Web } from 'sip.js';
import { Channel, CredentialsVoipAPI, MetadataVoipAPI, VoipExtension } from '../../models/channel.model';
import { ChannelTypeEnum } from '../../models/enum/channel.type';
import { ChannelService } from '../channel.service';

export type SoftphoneState = 'idle' | 'connecting' | 'registered' | 'dialing' | 'incall' | 'ended' | 'error';

/** Minimal identity of the contact currently being called — shared by the modal and the topbar pill. */
export interface SoftphoneContact {
  name: string;
  phone: string;
  image?: string;
}

// Default ICE servers — without STUN the browser only offers host candidates and media
// cannot traverse NAT (call connects over SIP but stays silent). TURN/per-channel config is Phase 2.
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

// If a call stays in 'dialing' past this window it almost certainly failed (no answer / ICE).
const DIAL_TIMEOUT_MS = 30000;

@Injectable({ providedIn: 'root' })
export class SoftphoneService {
  private simpleUser?: Web.SimpleUser;
  private registeredHost?: string;
  private dialWatchdog?: ReturnType<typeof setTimeout>;
  private durationTimer?: ReturnType<typeof setInterval>;

  // The remote <audio> sink lives in a single always-mounted host (SoftphoneHostComponent), not in the
  // trigger button — so SimpleUser keeps a stable audio node and the call survives navigation between pages.
  private audioElement?: HTMLAudioElement;
  private activeContactRef: SoftphoneContact | null = null;

  readonly state$ = new BehaviorSubject<SoftphoneState>('idle');
  readonly error$ = new BehaviorSubject<string | null>(null);

  /** True from the moment a call is started until it ends — drives the modal, the topbar pill and the disabled button. */
  readonly inProgress$ = new BehaviorSubject<boolean>(false);
  /** When true (and a call is in progress) the modal is hidden and the compact topbar pill is shown instead. */
  readonly minimized$ = new BehaviorSubject<boolean>(false);
  /** Elapsed seconds of the connected call (0 while ringing). */
  readonly duration$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly channelService: ChannelService,
    private readonly translateService: TranslateService,
  ) { }

  /** i18n helper — every message here reaches the user (alert/toast), so it is always translated. */
  private t(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  get isActive(): boolean {
    return this.state$.value === 'dialing' || this.state$.value === 'incall';
  }

  get activeContact(): SoftphoneContact | null {
    return this.activeContactRef;
  }

  /** Register the stable remote-audio sink. Called once by the always-mounted SoftphoneHostComponent. */
  attachAudio(audioElement: HTMLAudioElement): void {
    this.audioElement = audioElement;
  }

  /** Hide the call modal, keeping the call alive (compact pill takes over in the topbar). */
  minimize(): void {
    this.minimized$.next(true);
  }

  /** Re-open the call modal from the topbar pill. */
  restore(): void {
    this.minimized$.next(false);
  }

  /** Format a number of seconds as mm:ss for the modal and the pill. */
  formatDuration(totalSeconds: number): string {
    const safe = Math.max(0, totalSeconds ?? 0);
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(m)}:${pad(s)}`;
  }

  /** Lazily connect + REGISTER using the logged user's VoIP extension on the company's active channel. */
  async ensureRegistered(loggedUserId: string): Promise<void> {
    if (this.simpleUser) {
      return;
    }
    if (!this.audioElement) {
      throw new Error(this.t('softphone.errors.audioNotInitialized'));
    }
    const channel = await this.loadVoipChannel();
    if (!channel) {
      throw new Error(this.t('softphone.errors.noVoipChannel'));
    }
    const credentials = channel.credentials as CredentialsVoipAPI;
    if (!credentials.server || !credentials.port) {
      throw new Error(this.t('softphone.errors.incompleteCredentials'));
    }

    // per-user extension (ramal): match the logged user against metadata.extensions
    const extension = this.findUserExtension(channel, loggedUserId);
    if (!extension?.sipUsername) {
      throw new Error(this.t('softphone.errors.noExtension'));
    }

    const creds = await firstValueFrom(this.channelService.getMyExtensionCredentials(channel._id));

    const port = creds.port ?? 8089;
    const host = creds.server;
    const server = `wss://${host}:${port}/ws`;
    const domain = host;
    this.registeredHost = host;

    const options: Web.SimpleUserOptions = {
      aor: `sip:${creds.sipUsername}@${domain}`,
      media: {
        constraints: { audio: true, video: false },
        remote: { audio: this.audioElement },
      },
      userAgentOptions: {
        authorizationUsername: creds.sipUsername,
        authorizationPassword: creds.sipPassword,
        displayName: channel.name,
        // STUN/TURN ICE for WebRTC media — required for the audio path to traverse NAT.
        // Prefer the backend-configured servers (VOIP_STUN_URLS / VOIP_TURN_*); fall back to STUN-only.
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: (creds.iceServers?.length ? creds.iceServers : DEFAULT_ICE_SERVERS) as RTCIceServer[],
          },
        },
      },
    };

    this.simpleUser = new Web.SimpleUser(server, options);
    this.simpleUser.delegate = {
      onCallCreated: () => this.state$.next('dialing'),
      onCallAnswered: () => { this.clearDialWatchdog(); this.startDurationTimer(); this.state$.next('incall'); },
      onCallHangup: () => { this.endSession(); this.state$.next(this.simpleUser ? 'registered' : 'idle'); },
      onRegistered: () => this.state$.next('registered'),
      onUnregistered: () => this.state$.next('idle'),
      onServerDisconnect: () => {
        const wasInCall = this.inProgress$.value;
        this.endSession();
        this.state$.next('error');
        // Only alert when a live call dropped — a graceful disconnect (e.g. logout) must stay silent.
        if (wasInCall) {
          this.error$.next(this.t('softphone.errors.connectionLost'));
        }
      },
    };

    this.state$.next('connecting');
    await this.simpleUser.connect();
    await this.simpleUser.register();
  }

  /** Place a call to a contact using the logged user's registered extension. */
  async call(contact: SoftphoneContact, loggedUserId: string): Promise<void> {
    if (this.inProgress$.value) {
      return;
    }
    // Flip session state synchronously so the trigger button disables immediately (prevents double-click).
    this.error$.next(null);
    this.activeContactRef = contact;
    this.duration$.next(0);
    this.minimized$.next(false);
    this.inProgress$.next(true);
    try {
      await this.ensureRegistered(loggedUserId);
      const domain = this.registeredHost;
      const target = `sip:${this.normalize(contact.phone)}@${domain}`;
      this.state$.next('dialing');
      this.startDialWatchdog();
      await this.simpleUser!.call(target);
    } catch (error: any) {
      this.endSession();
      this.error$.next(error?.message ?? this.t('softphone.errors.callFailed'));
      this.state$.next('error');
      throw error;
    }
  }

  /** Whether the logged user has an active VoIP extension (ramal) on the company's active channel. */
  async hasExtensionForUser(loggedUserId: string): Promise<boolean> {
    const channel = await this.loadVoipChannel();
    return !!this.findUserExtension(channel, loggedUserId)?.sipUsername;
  }

  async hangup(): Promise<void> {
    try {
      await this.simpleUser?.hangup();
    } catch {}
    this.endSession();
    this.state$.next(this.simpleUser ? 'registered' : 'idle');
  }

  async disconnect(): Promise<void> {
    this.endSession();
    try {
      await this.simpleUser?.unregister();
      await this.simpleUser?.disconnect();
    } catch {}
    this.simpleUser = undefined;
    this.registeredHost = undefined;
    this.error$.next(null);
    this.state$.next('idle');
  }

  /** Reset all per-call session state (UI + timers). Keeps the SIP registration alive for the next call. */
  private endSession(): void {
    this.clearDialWatchdog();
    this.stopDurationTimer();
    this.duration$.next(0);
    this.activeContactRef = null;
    // Order matters: clear inProgress BEFORE minimized so the host's [inProgress, minimized]
    // reconcile never momentarily sees [true, false] and flashes a fresh dialog when a
    // minimized call ends.
    this.inProgress$.next(false);
    this.minimized$.next(false);
  }

  private startDurationTimer(): void {
    this.stopDurationTimer();
    this.duration$.next(0);
    this.durationTimer = setInterval(() => this.duration$.next(this.duration$.value + 1), 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = undefined;
    }
  }

  private startDialWatchdog(): void {
    this.clearDialWatchdog();
    this.dialWatchdog = setTimeout(() => {
      // Still in a call that never connected → fail it and notify, regardless of the exact intermediate state.
      if (this.inProgress$.value && this.state$.value !== 'incall') {
        this.error$.next(this.t('softphone.errors.callTimeout'));
        this.state$.next('error');
        this.hangup();
      }
    }, DIAL_TIMEOUT_MS);
  }

  private clearDialWatchdog(): void {
    if (this.dialWatchdog) {
      clearTimeout(this.dialWatchdog);
      this.dialWatchdog = undefined;
    }
  }

  // keep '*' and '#' so feature codes (e.g. *43 echo test) route correctly
  private normalize(phone: string): string {
    return phone.replace(/[^\d+*#]/g, '');
  }

  private async loadVoipChannel(): Promise<Channel | undefined> {
    const channels = await firstValueFrom(this.channelService.getList());
    return channels.find((c: Channel) => c.type === ChannelTypeEnum.VOIP_API && c.activated && !c.removed);
  }

  private findUserExtension(channel: Channel | undefined, loggedUserId: string): VoipExtension | undefined {
    const metadata = channel?.metadata as MetadataVoipAPI;
    return (metadata?.extensions ?? [])
      .find((ext: VoipExtension) => this.idOf(ext.user) === `${loggedUserId ?? ''}` && ext.activated);
  }

  /** Accepts a populated document, a raw ObjectId or a string. */
  private idOf(value: unknown): string {
    return `${(value as { _id?: unknown })?._id ?? value ?? ''}`;
  }
}
